/**
 * Golden logs: the file format in packages/game-core/test/golden/README.md.
 * A run's dungeon, party and starting bag, its command log, and the SHA-256
 * of the canonical JSON of the final state. Reading and writing files is the
 * caller's job (`tools/play` in Node, a test in the browser).
 */
import { type BagEntry, type Command, type PartyMember, type RunState, canonicalJson } from '@ab/game-core';

export type GoldenLog = {
  readonly dungeon: string;
  readonly party: readonly PartyMember[];
  /** The stacks the run starts with in its bag; left out for an empty bag. */
  readonly bag?: readonly BagEntry[];
  readonly commands: readonly Command[];
  /**
   * What the replay must show: event types (`damaged`) or `type:value` pairs
   * (`status_applied:burn`, an event of that type with a field equal to the
   * value). The recorder writes an empty list for the author to fill.
   */
  readonly expects?: readonly string[];
  readonly hash: string;
};

/**
 * SHA-256 (hex) of the UTF-8 text, through WebCrypto (`crypto.subtle`), which
 * Node 22 and every target browser have, so there is one implementation.
 */
export const sha256 = async (text: string): Promise<string> => {
  const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
};

/** The hash a golden log records: SHA-256 (hex) of `canonicalJson(state)`. */
export const stateHash = (state: RunState): Promise<string> => sha256(canonicalJson(state));

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** Parses a golden log, checking its shape (not the commands, which `apply` checks). */
export const parseLog = (text: string): GoldenLog => {
  const v: unknown = JSON.parse(text);
  if (!isRecord(v)) throw new Error('a golden log is a JSON object');
  const { dungeon, party, bag, commands, expects, hash } = v;
  if (typeof dungeon !== 'string') throw new Error('golden log: dungeon must be a string');
  if (!Array.isArray(party) || !party.every(isRecord)) throw new Error('golden log: party must be an array of objects');
  if (bag !== undefined && !(Array.isArray(bag) && bag.every(isRecord))) throw new Error('golden log: bag must be an array of objects');
  if (!Array.isArray(commands) || !commands.every(isRecord)) throw new Error('golden log: commands must be an array of objects');
  if (typeof hash !== 'string') throw new Error('golden log: hash must be a string');
  if (expects !== undefined && !(Array.isArray(expects) && expects.every((e) => typeof e === 'string'))) {
    throw new Error('golden log: expects must be an array of strings');
  }
  // Commands, party members and bag stacks arrive as JSON; game-core validates them at run time.
  const log: GoldenLog = {
    dungeon,
    party: party as unknown as PartyMember[],
    ...(bag === undefined ? {} : { bag: bag as unknown as BagEntry[] }),
    commands: commands as unknown as Command[],
    hash,
  };
  return expects === undefined ? log : { ...log, expects };
};

/** One flat object on one line: `{ "type": "move", "dir": "n" }`. */
const inline = (o: object): string =>
  `{ ${Object.entries(o)
    .map(([k, v]) => `${JSON.stringify(k)}: ${JSON.stringify(v)}`)
    .join(', ')} }`;

/**
 * The file text: one party member, bag stack and command per line, as the
 * checked-in logs are written. An empty bag is left out.
 */
export const formatLog = (log: GoldenLog): string =>
  [
    '{',
    `  "dungeon": ${JSON.stringify(log.dungeon)},`,
    '  "party": [',
    log.party.map((m) => `    ${inline(m)}`).join(',\n'),
    '  ],',
    ...(log.bag !== undefined && log.bag.length > 0 ? ['  "bag": [', log.bag.map((e) => `    ${inline(e)}`).join(',\n'), '  ],'] : []),
    '  "commands": [',
    log.commands.map((c) => `    ${inline(c)}`).join(',\n'),
    '  ],',
    `  "expects": [${(log.expects ?? []).map((e) => JSON.stringify(e)).join(', ')}],`,
    `  "hash": ${JSON.stringify(log.hash)}`,
    '}',
    '',
  ].join('\n');
